'use client'

import React from 'react'
// eslint-disable-next-line no-restricted-imports -- grandfathered; prefer next/image for new code
import { Box, Card, Flex, Image, Skeleton, Text, Title } from '@mantine/core'
import TestimonialNavigation from './TestimonialNavigation'
import { Testimonial } from '@/components/Store/StoreTestimonials'
import { motion } from 'framer-motion'

interface Props {
    testimonial: Testimonial
    onPrev: () => void
    onNext: () => void
    showControls?: boolean
    direction?: number // 1 for right (next), -1 for left (prev)
}

const cardHeight = { base: 800, sm: 800, md: 500 }

const TestimonialCard: React.FC<Props> = ({ testimonial, onPrev, onNext, showControls = true, direction = 1 }) => {
    const textVariants = {
        enter: {
            opacity: 0,
            x: direction === 1 ? 5 : -5,
            transition: { duration: 0.25 },
        },
        center: {
            opacity: 1,
            x: 0,
            transition: { duration: 0.25 },
        },
        exit: {
            opacity: 0,
            x: direction === 1 ? -5 : 5,
            transition: { duration: 0.25 },
        },
    }

    if (!testimonial) {
        return (
            <Flex direction={{ base: 'column', md: 'row' }} gap={24} w="100%" align="stretch">
                {/* Image Skeleton */}
                <Box
                    style={{
                        width: '100%',
                        maxWidth: '400px',
                        flex: '1 1 auto',
                        borderRadius: '16px',
                        overflow: 'hidden',
                    }}
                >
                    <Card
                        padding={0}
                        h={cardHeight}
                        w="100%"
                        bg="transparent"
                        shadow="none"
                        withBorder={false}
                        style={{
                            overflow: 'hidden',
                            borderRadius: '16px',
                        }}
                    >
                        <Skeleton height="100%" width="100%" radius="md" />
                    </Card>
                </Box>

                {/* Content Skeleton */}
                <Box
                    style={{
                        width: '100%',
                        maxWidth: '400px',
                        flex: '1 1 auto',
                    }}
                >
                    <Card
                        padding="xl"
                        h={cardHeight}
                        w="100%"
                        bg="var(--gm-background-bg-highlight-container)"
                        shadow="none"
                        withBorder={false}
                        style={{
                            overflow: 'hidden',
                            borderRadius: '16px',
                        }}
                    >
                        <Flex direction="column" gap={16} flex={1} justify="center" h="100%">
                            <Skeleton height={24} width="60%" radius="sm" />
                            <Skeleton height={32} width="90%" radius="sm" />
                            <Skeleton height={80} width="80%" radius="sm" />
                            <Skeleton height={20} width="40%" radius="sm" />
                        </Flex>
                    </Card>
                </Box>
            </Flex>
        )
    }

    return (
        <Flex direction={{ base: 'column', md: 'row' }} gap={24} w="100%" align="stretch">
            <Box
                style={{
                    width: '100%',
                    flex: '1 1 auto',
                    borderRadius: '16px',
                    overflow: 'hidden',
                }}
            >
                <Card
                    padding={0}
                    h={cardHeight}
                    w="100%"
                    bg="transparent"
                    shadow="none"
                    withBorder={false}
                    style={{
                        overflow: 'hidden',
                        borderRadius: '16px',
                    }}
                >
                    {renderImage()}
                </Card>
            </Box>

            {/* Content Card */}
            <Box
                style={{
                    width: '100%',
                    flex: '1 1 auto',
                }}
            >
                <Card
                    padding="xl"
                    h={cardHeight}
                    w="100%"
                    bg="var(--gm-background-bg-highlight-container)"
                    shadow="none"
                    withBorder={false}
                    style={{
                        overflow: 'hidden',
                        borderRadius: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <motion.div
                        key={`content-${testimonial.id}`}
                        variants={textVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        style={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Flex direction="column" gap={16}>
                            {renderLocation()}
                            {renderQuote()}
                            {renderDescription()}
                        </Flex>
                    </motion.div>

                    {showControls && (
                        <Box mt="auto">
                            <TestimonialNavigation onPrev={onPrev} onNext={onNext} isMobile={false} />
                        </Box>
                    )}
                </Card>
            </Box>
        </Flex>
    )

    function renderImage() {
        return (
            <Box
                style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                }}
            >
                <Box
                    style={{
                        position: 'absolute',
                        left: 0,
                        width: '100%',
                        height: '120%',
                    }}
                >
                    <Image
                        src={`https://${process.env.NEXT_PUBLIC_ASSETS_DOMAIN}/${testimonial.desktopImageUrl}`}
                        alt={testimonial.customerName}
                        width="100%"
                        height="100%"
                        fit="cover"
                        style={{
                            objectFit: 'cover',
                            objectPosition: 'center',
                        }}
                    />
                </Box>
            </Box>
        )
    }

    function renderLocation() {
        return (
            <Title order={3} size="18px" c="var(--gm-foreground-fg-tertiary)" fw="var(--gm-weight-text)">
                {testimonial.customerName} in {testimonial.location}
            </Title>
        )
    }

    function renderQuote() {
        return (
            <Text
                size="28px"
                fw="var(--gm-weight-small)"
                lh="120%"
                c="var(--gm-foreground-fg-primary)"
                style={{ letterSpacing: 'var(--gm-display-m-letter-spacing)' }}
            >
                &quot;{testimonial.quote}&quot;
            </Text>
        )
    }

    function renderDescription() {
        return (
            <Text
                size="16px"
                c="var(--gm-foreground-fg-secondary)"
                fw="var(--gm-weight-text)"
                lh="var(--gm-text-l-line-height)"
            >
                {testimonial.description}
            </Text>
        )
    }
}

export default TestimonialCard
